import { DP_FILLER, MA_HONORIFIC, dpNameWord } from "./enum-slot.js";
import { isFirstPersonPronoun } from "./member-slot.js";
import { sameListedWord, sameWord, wordsOf, type Word } from "./vi-words.js";

/**
 * 🔴 UX-SMALL-WORDING (V-5 · @UC0161 @UC0320) — chữ của ô NGƯỜI có NÊU một người không (một tên, hay một lời gọi người: "anh", "chị", "mình").
 *
 * Câu hỏi lại *"Mình chưa rõ bạn nói ai."* (`aiClarifyUnmatchedMemberText`) chỉ đúng khi người dùng ĐÃ nói một người mà app không khớp được. Đo B0 (LLM
 * thật, 16/10): Phiếu gạch ô người bằng chữ KHÔNG nói ai — cả câu *"bao da chuyen tien"* (ô `be` của PAY-REPORT) · *"xac nhan da nhan tien"* · chữ chung
 * *"thanh vien"* (ô `nguoi` của PAY-CONFIRM-DECIDE) ⇒ resolver `unmatched` ⇒ app trách người dùng "chưa rõ bạn nói ai" khi họ không nói ai. Hàm này chỉ
 * đổi CÂU (tầng api `clarifyTurn`): lý do `unmatched` vẫn giữ nguyên ở ô (không lớp nào tự điền từ nó — #1029), danh sách vẫn là danh sách resolver.
 *
 * Luật: còn ÍT NHẤT một chữ có thể là (một phần) TÊN (`dpNameWord` — cùng vị-từ chữ-tên của chốt PCD-HARDEN, một nguồn: ngoài ngữ pháp khuôn tiền về ·
 * đại từ/chữ chung · thời gian · phủ định · số · `DP_FILLER`) hoặc một lời gọi người (xưng hô · đại từ ngôi 1), SAU KHI bỏ chữ của VIỆC (tập đóng dưới).
 * Chữ lạ bị coi là tên ⇒ câu cũ "chưa rõ bạn nói ai" (đúng như trước) — chiều sai chỉ là câu chữ, không bao giờ một người.
 */
const ACTION_PHRASES: readonly (readonly Word[])[] = ["xác nhận", "từ chối", "thành viên", "hội viên", "phụ huynh", "mọi người", "đóng hộ", "rút lại"].map(wordsOf);
/** Chữ ĐƠN của việc/chữ chung không bao giờ là tên — tập ĐÓNG, chỉ thêm khi đo được. */
const ACTION_WORDS: readonly Word[] = wordsOf("duyệt ghi nhắc member mem các mấy những");

const isFiller = (x: Word): boolean => DP_FILLER.some((f) => sameWord(x, f));

export function memberTextNamesSomeone(text: string): boolean {
  const w = wordsOf(text);
  // review V-5 M1: MỘT chữ gõ không dấu đứng riêng ("bao") — có thể là tên ("Bảo") trùng mặt chữ việc ("báo") ⇒ coi là nêu người (câu cũ), trừ chữ việc thuần.
  if (w.length === 1 && w[0]!.plain && !ACTION_WORDS.some((a) => sameListedWord(w[0]!, a))) return true;
  const inPhrase = new Set<number>();
  for (const p of ACTION_PHRASES) {
    for (let i = 0; i + p.length <= w.length; i += 1) {
      if (p.every((x, j) => sameListedWord(w[i + j]!, x))) for (let j = 0; j < p.length; j += 1) inPhrase.add(i + j);
    }
  }
  return w.some((x, i) => {
    if (inPhrase.has(i) || ACTION_WORDS.some((a) => sameListedWord(x, a))) return false;
    return dpNameWord(x, isFiller) || MA_HONORIFIC.some((h) => sameWord(x, h)) || isFirstPersonPronoun(x.accented);
  });
}

/**
 * 🔴 AI-INTERP C3 re-review sec M-1 (@UC0201 @UC0161) — chữ của ô người CHỈ CẢ NHÓM («mọi người», «các phụ huynh còn thiếu», «những bạn chưa đóng»)?
 *
 * Đây là quyết định có HỆ QUẢ PHẠM VI: ô người opt-in `unnamedMeansBlank` (DEBT-REMIND.nguoi) coi chữ đó là VẮNG ô ⇒ nhắc CẢ đợt. Vì vậy KHÔNG dùng
 * `memberTextNamesSomeone`/`dpNameWord` ở trên (chỉnh cho CÂU CHỮ của câu hỏi — «chiều sai chỉ là câu chữ»; đo 02/11 qua `dpNameWord`: «nó», «người đó»,
 * «ai đó», tên thật «Định», «Tài», «Sáng» ⇒ «không nêu ai» ⇒ nhắc cả đợt). Ở đây là DANH SÁCH TRẮNG ĐÓNG — chiều sai duy nhất được phép là HỎI.
 * Mọi chữ phải thuộc một trong: lượng từ số nhiều · danh từ người/tập thể (cụm cặp «anh chị», «ba mẹ»… là MỘT danh từ) · cụm trạng thái chưa đóng
 * («chưa đóng», «còn thiếu»… — chữ đơn «đóng»/«nợ» không: «các bạn Đông» gõ không dấu là «cac ban dong») · lời đệm. Chữ lạ ⇒ `false`.
 *
 * 🔴 re-review vòng 3 MEDIUM — tên riêng trùng MẶT CHỮ một từ của danh sách (Nhung⇒những · May⇒mấy · Ba⇒ba · Nha⇒nhà · Vân⇒vẫn · «cả» = «cũng»). Năm luật:
 *  (a) so CHẶT: chữ của ô có dấu ở bất kỳ từ nào ⇒ MỌI từ phải trùng cả dấu (từ không dấu không còn «nới» thành từ có dấu); chỉ chữ KHÔNG DẤU HOÀN TOÀN
 *      mới so theo khoá bỏ dấu (`sameListedWord`);
 *  (b) VỊ TRÍ: có lượng từ; không danh từ nào đứng trước lượng từ đầu tiên; mỗi lượng từ đứng NGAY TRƯỚC một danh từ hoặc lượng từ khác («tất cả mọi người»);
 *      «tất cả» là một khối — đứng riêng được (`ALL_OF_THEM`);
 *  (c) «cả» đứng riêng chỉ là số nhiều NGAY TRƯỚC danh từ tập thể (nhóm/lớp/đội/nhà) — «nhắc cả mẹ bé luôn» = «cũng»; «ba»/«má» đứng riêng chỉ là danh từ
 *      NGAY SAU lượng từ («các ba», «mấy má»);
 *  (d) hai danh từ liền nhau (không phải cặp đã khai) là sở hữu/đồng vị — MỘT hộ/MỘT người: «mấy bé nhà cô», «các bạn Anh»;
 *  (e) từ VIẾT HOA đầu ở giữa chữ là tên riêng («các bạn Nha» — «nha» trùng lời đệm kể cả khi so chặt).
 * Tập ĐÓNG, chỉ thêm khi đo được trên câu thật.
 */
const GROUP_PLURALS: readonly Word[] = wordsOf("mọi các những mấy hết");
/** Danh từ người dạng CỤM — gồm các CẶP xưng hô dùng chung («anh chị», «ba mẹ»): một danh từ, không phải hai danh từ liền nhau (luật d). */
const GROUP_NOUN_PHRASES: readonly (readonly Word[])[] = [
  "phụ huynh",
  "thành viên",
  "hội viên",
  "gia đình",
  "học sinh",
  "anh chị em",
  "anh chị",
  "anh em",
  "chị em",
  "ông bà",
  "bố mẹ",
  "ba mẹ",
  "cha mẹ",
  "cô chú",
  "cô bác",
  "chú bác",
].map(wordsOf);
const GROUP_NOUNS: readonly Word[] = wordsOf("người ai nhà lớp nhóm đội anh chị em cô chú bác bạn bé con cháu ông bà bố mẹ");
/** Luật (c): danh từ chỉ đứng làm danh từ NGAY SAU lượng từ (đứng chỗ khác là tên riêng thường gặp — «chị Ba»). */
const GROUP_NOUNS_AFTER_PLURAL_ONLY: readonly Word[] = wordsOf("ba má");
/** Luật (c): danh từ TẬP THỂ — «cả» đứng ngay trước chúng mới là số nhiều («cả lớp», «cả nhà»). */
const GROUP_COLLECTIVES: readonly Word[] = wordsOf("nhóm lớp đội nhà");
const WORD_CA: readonly Word[] = wordsOf("cả");
const GROUP_STATE_PHRASES: readonly (readonly Word[])[] = [
  "chưa đóng",
  "chưa nộp",
  "chưa trả",
  "chưa chuyển",
  "còn thiếu",
  "còn nợ",
  "đang nợ",
  "đang thiếu",
  "tiền quỹ",
].map(wordsOf);
const GROUP_FILLERS: readonly Word[] = wordsOf("còn vẫn tiền quỹ đợt này trong của nhắc giục hối ping nhé nha nhe đi giùm giúp hộ với ạ ơi luôn");
const ALL_OF_THEM: readonly Word[] = wordsOf("tất cả");

type GroupToken = "plural" | "all" | "noun" | "other";

export function memberTextIsGroupPhrase(text: string): boolean {
  const nfc = text.normalize("NFC");
  const w = wordsOf(nfc);
  if (w.length === 0) return false;
  // (e) VIẾT HOA đầu ở giữa chữ ⇒ tên riêng.
  if (w.some((x, i) => i > 0 && /^\p{Lu}/u.test(nfc.slice(x.start, x.end)))) return false;
  // (a) chữ có dấu ở bất kỳ từ nào ⇒ so CHẶT (hình chữ + dấu thanh); chữ không dấu hoàn toàn ⇒ so theo khoá bỏ dấu.
  const strict = w.some((x) => !x.plain);
  const eq = (x: Word, listed: Word): boolean => (strict ? x.shape === listed.shape && x.tone === listed.tone : sameListedWord(x, listed));
  const inSet = (x: Word | undefined, set: readonly Word[]): boolean => x !== undefined && set.some((l) => eq(x, l));
  const startsWith = (i: number, p: readonly Word[]): boolean => i + p.length <= w.length && p.every((x, j) => eq(w[i + j]!, x));

  // Gộp từ thành KHỐI theo thứ tự: «tất cả» · cụm danh từ · cụm trạng thái · từ đơn. Từ không thuộc khối nào ⇒ chữ lạ ⇒ false.
  const tokens: GroupToken[] = [];
  for (let i = 0; i < w.length; ) {
    if (startsWith(i, ALL_OF_THEM)) {
      tokens.push("all");
      i += ALL_OF_THEM.length;
      continue;
    }
    const nounPhrase = GROUP_NOUN_PHRASES.find((p) => startsWith(i, p));
    if (nounPhrase !== undefined) {
      tokens.push("noun");
      i += nounPhrase.length;
      continue;
    }
    const statePhrase = GROUP_STATE_PHRASES.find((p) => startsWith(i, p));
    if (statePhrase !== undefined) {
      tokens.push("other");
      i += statePhrase.length;
      continue;
    }
    const x = w[i]!;
    const prev = tokens[tokens.length - 1];
    if (inSet(x, GROUP_PLURALS)) tokens.push("plural");
    else if (inSet(x, WORD_CA) && inSet(w[i + 1], GROUP_COLLECTIVES)) tokens.push("plural"); // (c) «cả lớp»
    else if (inSet(x, GROUP_NOUNS)) tokens.push("noun");
    else if (inSet(x, GROUP_NOUNS_AFTER_PLURAL_ONLY) && prev === "plural") tokens.push("noun"); // (c) «các ba»
    else if (inSet(x, GROUP_FILLERS)) tokens.push("other");
    else return false; // chữ lạ (tên · đại từ · số · ký hiệu · «cả» = «cũng») ⇒ không phải cụm chung ⇒ hỏi
    i += 1;
  }
  // (b) VỊ TRÍ + (d) hai danh từ liền nhau.
  const firstQuantifier = tokens.findIndex((t) => t === "plural" || t === "all");
  if (firstQuantifier < 0) return false;
  if (tokens.slice(0, firstQuantifier).includes("noun")) return false;
  for (let t = 0; t < tokens.length; t += 1) {
    const next = tokens[t + 1];
    if (tokens[t] === "plural" && next !== "noun" && next !== "plural" && next !== "all") return false;
    if (tokens[t] === "noun" && next === "noun") return false;
  }
  return tokens.includes("noun") || tokens.includes("all");
}

/**
 * 🔴 AI-INTERP I2 vá review H1 (@UC0173 @UC0161) — câu trả lời cho Ô TÊN (người · quỹ · đợt · nhóm) PHỦ ĐỊNH DANH TÍNH rõ ⇒ không phải một tên để điền.
 *
 * Vì sao KHÔNG dùng `hasNegationOrDoubt` (vị-từ của ô enum/ngày): tập chữ của nó có trợ từ CUỐI CÂU và chữ phủ định bỏ dấu trùng đúng TÊN NGƯỜI —
 * «Nhi»/«Yến Nhi» (= nhỉ) · «Hà»/«Thu Hà» (= hà) · «Dung»/«chị Dung» (= dừng/đừng) · «Hong» (= hông) ⇒ câu trả lời ô tên bị hỏi lại MÃI, gõ lại đúng tên
 * vẫn vậy (bản vá I2 đầu). Ô tên chỉ cần chặn đúng một thứ: lời CHỐI một tên ("không phải Lan" — bộ khớp tên vẫn ra "Lan").
 *
 * Luật — tập ĐÓNG, cố ý hẹp: dấu `?` ở đâu cũng được ("Lan à?"), hoặc cặp liền «không · ko · k · chẳng · chả · đâu» + «phải» (so cả dạng bỏ dấu:
 * "ko phai", "khong phai"). Không trợ từ cuối câu, không token mơ hồ. Cái MẤT (đã cân): lời chối không có «phải» ("không, Lan cơ") đi tới resolver
 * như một câu trả lời — resolver khớp trên danh sách THẬT (0/≥2 ⇒ hỏi), thẻ xác nhận là chốt cuối.
 */
const NAME_DENIAL_HEADS: ReadonlySet<string> = new Set(["không", "ko", "k", "chẳng", "chả", "đâu"]);
const NAME_DENIAL_HEAD_KEYS: ReadonlySet<string> = new Set(["khong", "ko", "k", "chang", "cha", "dau"]);

/** Chữ còn dấu phải đúng chữ phủ định; chữ gõ KHÔNG dấu so dạng bỏ dấu. */
const isDenialHead = (w: Word): boolean => (w.plain ? NAME_DENIAL_HEAD_KEYS.has(w.key) : NAME_DENIAL_HEADS.has(w.accented));
const isPhai = (w: Word): boolean => (w.plain ? w.key === "phai" : w.accented === "phải");

export function nameAnswerIsDenied(text: string): boolean {
  if (text.includes("?")) return true;
  const w = wordsOf(text);
  return w.some((x, i) => i + 1 < w.length && isDenialHead(x) && isPhai(w[i + 1]!));
}
